/**
 * Bundle 95.1 - Campaign schema drift tile on Admin Data Health.
 *
 * Renders one row per event_survey_campaigns doc with pass/fail pills
 * on the Bundle 89 fingerprint checks (step titles, comparison field,
 * future_event_likelihood type, discovery option count, discovery
 * label). Fires GET /api/admin/health/schema-drift on mount; a
 * "Refresh Schema" button POSTs to /api/admin/survey/refresh-schema
 * with force=true to rewrite stale snapshots and then reloads the
 * drift report.
 *
 * No em/en dashes. No `eslint-disable`. No `photo`/`image` in alt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, GitCompare, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/utils/api';


const Pill = ({ ok, label, testid }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
    }`}
    data-testid={testid}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
    {label}
  </span>
);


const SchemaDriftTile = () => {
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get('/admin/health/schema-drift?force=true');
      setData(r);
    } catch (e) {
      setData({ rows: [], drifted_count: 0, total_scanned: 0,
        error: e?.response?.data?.detail || 'Failed to load drift report.' });
    }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const runRefresh = useCallback(async () => {
    setRefreshing(true);
    setFlash(null);
    try {
      const { data: r } = await api.post('/admin/survey/refresh-schema',
        { force: true, dry_run: false });
      setFlash({
        ok: true,
        msg: `Scanned ${r.total_scanned}; updated ${r.updated}; unchanged ${r.unchanged}; skipped ${r.skipped_immutable}.`,
      });
      await load();
    } catch (e) {
      setFlash({ ok: false,
        msg: e?.response?.data?.detail || 'Refresh failed.' });
    }
    setRefreshing(false);
  }, [load]);

  const drifted = data?.drifted_count ?? 0;
  const total = data?.total_scanned ?? 0;
  const allFresh = drifted === 0 && total > 0;

  return (
    <Card data-testid="tile-schema-drift">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GitCompare className="h-4 w-4 text-primary-700" aria-hidden="true" />
            <div className="font-display font-semibold text-primary-900">
              Survey Schema Drift
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load}
              disabled={busy}
              data-testid="schema-drift-reload">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />
              Reload
            </Button>
            <Button variant="default" size="sm" onClick={runRefresh}
              disabled={refreshing}
              data-testid="schema-drift-run-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Schema
            </Button>
          </div>
        </div>

        <div className="mb-3 flex items-center gap-3 text-sm">
          {allFresh ? (
            <span className="inline-flex items-center gap-1 text-green-800"
              data-testid="schema-drift-summary-ok">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              All {total} campaigns fresh
            </span>
          ) : drifted > 0 ? (
            <span className="inline-flex items-center gap-1 text-red-800"
              data-testid="schema-drift-summary-drift">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              {drifted} of {total} campaigns drifted
            </span>
          ) : (
            <span className="text-primary-700"
              data-testid="schema-drift-summary-empty">
              No campaigns yet.
            </span>
          )}
        </div>

        {flash ? (
          <div
            className={`mb-3 rounded-md border px-3 py-2 text-xs ${
              flash.ok ? 'bg-green-50 border-green-200 text-green-800'
                       : 'bg-red-50 border-red-200 text-red-800'
            }`}
            data-testid="schema-drift-flash">
            {flash.msg}
          </div>
        ) : null}

        {data?.error ? (
          <div className="text-xs text-red-700"
            data-testid="schema-drift-error">
            {data.error}
          </div>
        ) : null}

        {(data?.rows || []).length > 0 ? (
          <div className="space-y-2">
            {data.rows.map((row) => (
              <div key={row.campaign_id}
                className="rounded-md border border-primary-100 p-2 text-xs"
                data-testid={`schema-drift-row-${row.campaign_id.slice(0, 8)}`}>
                <div className="flex items-center justify-between mb-1">
                  <code className="text-[11px] text-primary-800">
                    {row.campaign_id.slice(0, 8)}
                  </code>
                  <Pill ok={!row.drift}
                    label={row.drift ? 'Drifted' : 'Fresh'}
                    testid={`pill-drift-${row.campaign_id.slice(0, 8)}`} />
                </div>
                <div className="text-[11px] text-primary-700">
                  stored={row.stored_sha256} fresh={row.fresh_sha256}
                  {' · '}q={row.attendee_question_count}
                  {' · '}step_titles={row.step_titles_count}
                </div>
                {row.missing_fingerprints?.length ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {row.missing_fingerprints.map((m) => (
                      <Pill key={m} ok={false} label={m}
                        testid={`pill-missing-${m}`} />
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default SchemaDriftTile;
